// src/App.js
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { Provider } from "react-redux";
import { Toaster } from "react-hot-toast";

// Store
import { store } from "./store";

// Context Providers
import { AuthProvider } from "./context/AuthContext";

// Layout Components
import MainLayout from "./components/layouts/MainLayout";
import AuthLayout from "./components/layouts/AuthLayout";
import PublicAccessLayout from "./components/layouts/PublicAccessLayout";

// Route Guards
import ProtectedRoute from "./components/auth/ProtectedRoute";
import PublicRoute from "./components/auth/PublicRoute";

// Pages - Authentication
import LoginPage from "./pages/auth/LoginPage";

// Pages - Dashboard
import Dashboard from "./pages/dashboard/Dashboard";

// Pages - Clients
import ClientsPage from "./pages/clients/ClientsPage";
import ClientDetailsPage from "./pages/clients/ClientDetailsPage";
import ClientFormPage from "./pages/clients/ClientFormPage";

// Pages - Services
import ServicesPage from "./pages/services/ServicesPage";
import ServiceDetailsPage from "./pages/services/ServiceDetailsPage";
import ServiceFormPage from "./pages/services/ServiceFormPage";

// Pages - Contracts
import ContractsPage from "./pages/contracts/ContractsPage";
import ContractDetailsPage from "./pages/contracts/ContractDetailsPage";
import ContractFormPage from "./pages/contracts/ContractFormPage";

// Pages - Templates
import TemplatesPage from "./pages/templates/TemplatesPage";
import TemplateDetailsPage from "./pages/templates/TemplateDetailsPage";
import TemplateEditorPage from "./pages/templates/TemplateEditorPage";

// Pages - Public Access
import CodeVerificationPage from "./pages/public-access/CodeVerificationPage";
import DocumentViewerPage from "./pages/public-access/DocumentViewerPage";

// Pages - Settings
import SettingsPage from "./pages/settings/SettingsPage";
import CompanyInfoPage from "./pages/settings/CompanyInfoPage";
import ProfilePage from "./pages/settings/ProfilePage";

// Error Pages
import NotFoundPage from "./pages/errors/NotFoundPage";
import ErrorBoundary from "./components/common/ErrorBoundary";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import TestPublicAccessPage from "./pages/test/TestPublicAccessPage";
import BulkContractsPage from './pages/bulk-contracts/BulkContractsPage';
import StandaloneActsPage from './pages/standalone-acts/StandaloneActsPage';
import ClientContractsDashboard from './pages/public-access/ClientContractsDashboard';

import AccessRequestedPage from "./pages/public-access/AccessRequestedPage";
import AccessErrorPage from "./pages/public-access/AccessErrorPage";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <Router>
              <div className="App">
                <Routes>
                  {/* Public Access Routes (No Authentication Required) */}
                  <Route
                    path="/contract-access/:token"
                    element={<CodeVerificationPage />}
                  />
                  <Route
                    path="/contract-access/:token/view"
                    element={<DocumentViewerPage />}
                  />
                  <Route
                    path="/contract-access/:token/dashboard"
                    element={<ClientContractsDashboard />}
                  />

                  <Route path="/access-requested" element={<AccessRequestedPage />} />
                  <Route path="/access-error" element={<AccessErrorPage />} />

                  {/* Authentication Routes */}
                  <Route
                    path="/login"
                    element={
                      <PublicRoute>
                        <AuthLayout>
                          <LoginPage />
                        </AuthLayout>
                      </PublicRoute>
                    }
                  />

                  <Route
                    path="/forgot-password"
                    element={
                      <PublicRoute>
                        <AuthLayout>
                          <ForgotPassword />
                        </AuthLayout>
                      </PublicRoute>
                    }
                  />

                  <Route
                    path="/reset-password"
                    element={
                      <PublicRoute>
                        <AuthLayout>
                          <ResetPassword />
                        </AuthLayout>
                      </PublicRoute>
                    }
                  />

                  {/* Protected Routes (Authentication Required) */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <Navigate to="/dashboard" replace />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Dashboard */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <Dashboard />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Clients Routes */}
                  <Route
                    path="/clients"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <ClientsPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/clients/new"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <ClientFormPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/clients/:id"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <ClientDetailsPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/clients/:id/edit"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <ClientFormPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Services Routes */}
                  <Route
                    path="/services"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <ServicesPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/services/new"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <ServiceFormPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/services/:id"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <ServiceDetailsPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/services/:id/edit"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <ServiceFormPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Contracts Routes */}
                  <Route
                    path="/contracts"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <ContractsPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/contracts/new"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <ContractFormPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/contracts/bulk"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <MainLayout>
                          <ContractFormPage bulk={true} />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/contracts/:id"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <ContractDetailsPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/contracts/:id/edit"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <ContractFormPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/bulk-contracts"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <BulkContractsPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/standalone-acts"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <StandaloneActsPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />


                  {/* Templates Routes */}
                  <Route
                    path="/templates"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <TemplatesPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/templates/new"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <MainLayout>
                          <TemplateEditorPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/templates/:id"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <TemplateDetailsPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/templates/:id/edit"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <MainLayout>
                          <TemplateEditorPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Settings Routes */}
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <SettingsPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/settings/company"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <MainLayout>
                          <CompanyInfoPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/settings/profile"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <ProfilePage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/test/public-access"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <TestPublicAccessPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />

                  {/* 404 Page */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>

                {/* Global Toast Notifications */}
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: "#363636",
                      color: "#fff",
                    },
                    success: {
                      duration: 3000,
                      iconTheme: {
                        primary: "#10B981",
                        secondary: "#fff",
                      },
                    },
                    error: {
                      duration: 5000,
                      iconTheme: {
                        primary: "#EF4444",
                        secondary: "#fff",
                      },
                    },
                  }}
                />
              </div>
            </Router>
          </AuthProvider>

          {/* React Query Devtools (only in development) */}
          {process.env.NODE_ENV === "development" && (
            <ReactQueryDevtools initialIsOpen={false} />
          )}
        </QueryClientProvider>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
